// src/utils/enhancedQuizUtils.ts
import { ProcessItem } from "@/types";
import { 
  EnhancedQuizQuestion,
  EnhancedQuizQuestionType,
  QuizDifficulty,
  QuizAnswer
} from "@/types/quiz";
import { parseParticipantsWithResponsibilities, parseDocumentLinks } from "@/utils/processUtils";

/**
 * Huvudfunktion för att generera förbättrade quizfrågor
 */
export const generateEnhancedQuizQuestions = (
  processData: ProcessItem[],
  questionCount: number = 10,
  difficulty: QuizDifficulty = 'mixed'
): EnhancedQuizQuestion[] => {
  if (!processData || processData.length === 0) {
    return [];
  }
  
  try {
    // Alla tillgängliga frågetyper inklusive nya typer
    const allQuestionTypes: EnhancedQuizQuestionType[] = [
      // Ursprungliga typer
      'main-process',
      'sub-process',
      'moment',
      'participant',
      'document',
      'important',
      // Nya typer
      'fill-in-blank',
      'sequence',
      'relation',
      'detailed-description'
    ];
    
    // Filtrera frågetyper baserat på tillgänglig data
    const activeQuestionTypes = allQuestionTypes.filter(type => hasDataForQuestionType(processData, type));
    
    if (activeQuestionTypes.length === 0) {
      console.warn("Ingen lämplig data för att generera frågor");
      return [];
    }
    
    // Skapa frågor av alla tillgängliga typer
    let allQuestions: EnhancedQuizQuestion[] = [];
    
    // Generera frågor för varje typ
    activeQuestionTypes.forEach(type => {
      const questionsForType = generateQuestionsForType(processData, type, difficulty);
      allQuestions = [...allQuestions, ...questionsForType];
    });
    
    if (allQuestions.length === 0) {
      console.warn("Kunde inte generera några frågor");
      return [];
    }
    
    // Filtrera efter svårighetsnivå om det inte är 'mixed'
    if (difficulty !== 'mixed') {
      allQuestions = allQuestions.filter(q => 
        !q.difficulty || q.difficulty === difficulty
      );
    }
    
    // Blanda och begränsa antalet frågor
    const shuffledQuestions = shuffleArray(allQuestions);
    return shuffledQuestions.slice(0, Math.min(questionCount, shuffledQuestions.length));
  } catch (error) {
    console.error("Fel vid generering av quizfrågor:", error);
    return [];
  }
};

/**
 * Kontrollerar om det finns lämplig processdata för en specifik frågetyp
 */
const hasDataForQuestionType = (
  processData: ProcessItem[],
  questionType: EnhancedQuizQuestionType
): boolean => {
  switch (questionType) {
    case 'main-process':
      // Kräver minst en huvudprocess
      return [...new Set(processData.map(item => item.Title))].length > 1;
      
    case 'sub-process':
      // Kräver minst en delprocess
      return [...new Set(processData.map(item => item.field_0))].length > 1;
      
    case 'moment':
      // Kräver minst ett moment
      return [...new Set(processData.map(item => item.field_2))].length > 1;
      
    case 'participant':
      // Kräver minst en deltagare i något moment
      return processData.some(item => 
        (item.Deltagareochansvar1 && item.Deltagareochansvar1.trim() !== "") ||
        (item.Deltagareochansvar2 && item.Deltagareochansvar2.trim() !== "") ||
        (item.field_5 && item.field_5.trim() !== "")
      );
      
    case 'document':
      // Kräver minst ett dokument i något moment
      return processData.some(item => item.field_4 && item.field_4.trim() !== "");
      
    case 'important':
      // Kräver minst ett viktigt moment
      return processData.some(item => item.Viktigt && item.Viktigt.trim() !== "");
      
    case 'detailed-description':
      // Kräver minst en detaljerad beskrivning
      return processData.some(item => item.Detaljeradbeskrivning && item.Detaljeradbeskrivning.trim() !== "");
      
    case 'fill-in-blank':
      // Kräver minst en beskrivning för att skapa fill-in-blank
      return processData.some(item => item.field_3 && item.field_3.trim().length > 20);
      
    case 'sequence':
      // Kräver minst 4 objekt i samma process för att skapa en sekvensfråga
      const subProcessGroups = processData.reduce((acc, item) => {
        const key = `${item.Title}_${item.field_0}`;
        if (!acc[key]) acc[key] = 0;
        acc[key]++;
        return acc;
      }, {} as Record<string, number>);
      
      return Object.values(subProcessGroups).some(count => count >= 4);
      
    case 'relation':
      // Kräver både deltagare och dokument för relationsfrågor
      return processData.some(item => 
        ((item.Deltagareochansvar1 && item.Deltagareochansvar1.trim() !== "") ||
         (item.Deltagareochansvar2 && item.Deltagareochansvar2.trim() !== "")) &&
        (item.field_4 && item.field_4.trim() !== "")
      );
      
    default:
      return false;
  }
};

/**
 * Genererar en uppsättning frågor av en specifik typ
 */
const generateQuestionsForType = (
  processData: ProcessItem[],
  questionType: EnhancedQuizQuestionType,
  difficulty: QuizDifficulty
): EnhancedQuizQuestion[] => {
  // Antalet frågor att generera per typ
  const questionCountPerType = 5;
  
  // Filtrera data som är relevant för frågetypen
  const relevantData = filterRelevantData(processData, questionType);
  
  if (relevantData.length === 0) {
    return [];
  }
  
  // Generera olika typer av frågor baserat på questionType
  let questions: EnhancedQuizQuestion[] = [];
  
  switch(questionType) {
    case 'main-process':
      questions = generateMainProcessQuestions(relevantData, questionCountPerType);
      break;
    case 'sub-process':
      questions = generateSubProcessQuestions(relevantData, questionCountPerType);
      break;
    case 'moment':
      questions = generateMomentQuestions(relevantData, questionCountPerType);
      break;
    case 'participant':
      questions = generateParticipantQuestions(relevantData, questionCountPerType);
      break;
    case 'document':
      questions = generateDocumentQuestions(relevantData, questionCountPerType);
      break;
    case 'important':
      questions = generateImportantQuestions(relevantData, questionCountPerType);
      break;
    case 'detailed-description':
      questions = generateDetailedDescriptionQuestions(relevantData, questionCountPerType);
      break;
    case 'fill-in-blank':
      questions = generateFillInBlankQuestions(relevantData, questionCountPerType);
      break;
    case 'sequence':
      questions = generateSequenceQuestions(relevantData, questionCountPerType);
      break;
    case 'relation':
      questions = generateRelationQuestions(relevantData, questionCountPerType);
      break;
  }
  
  // Tilldela svårighetsgrad till varje fråga
  return assignDifficulty(questions, difficulty);
};

/**
 * Filtrerar data som är relevant för en specifik frågetyp
 */
const filterRelevantData = (
  processData: ProcessItem[],
  questionType: EnhancedQuizQuestionType
): ProcessItem[] => {
  switch (questionType) {
    case 'main-process':
      // För huvudprocesser behöver vi bara unika huvudprocesser
      return getUniqueItemsByField(processData, 'Title');
      
    case 'sub-process':
      // För delprocesser behöver vi unika kombinationer av huvudprocess + delprocess
      return getUniqueItemsByFields(processData, ['Title', 'field_0']);
      
    case 'moment':
      // För moment behöver vi bara data med moment
      return processData.filter(item => item.field_2 && item.field_2.trim() !== "");
      
    case 'participant':
      // För deltagare behöver vi data med deltagare
      return processData.filter(item => 
        (item.Deltagareochansvar1 && item.Deltagareochansvar1.trim() !== "") ||
        (item.Deltagareochansvar2 && item.Deltagareochansvar2.trim() !== "") ||
        (item.field_5 && item.field_5.trim() !== "")
      );
      
    case 'document':
      // För dokument behöver vi data med dokument
      return processData.filter(item => item.field_4 && item.field_4.trim() !== "");
      
    case 'important':
      // För viktigt behöver vi data med Viktigt-märkning
      return processData.filter(item => item.Viktigt && item.Viktigt.trim() !== "");
      
    case 'detailed-description':
      // För detaljerade beskrivningar behöver vi data med sådana
      return processData.filter(item => item.Detaljeradbeskrivning && item.Detaljeradbeskrivning.trim() !== "");
      
    case 'fill-in-blank':
      // För fill-in-blank behöver vi data med beskrivningar som är tillräckligt långa
      return processData.filter(item => item.field_3 && item.field_3.trim().length > 20);
      
    case 'sequence':
      // För sekvens behöver vi grupper av moment inom samma process
      return processData;
      
    case 'relation':
      // För relation behöver vi data med både deltagare och dokument
      return processData.filter(item => 
        ((item.Deltagareochansvar1 && item.Deltagareochansvar1.trim() !== "") ||
         (item.Deltagareochansvar2 && item.Deltagareochansvar2.trim() !== "")) &&
        (item.field_4 && item.field_4.trim() !== "")
      );
      
    default:
      return processData;
  }
};

/**
 * Ger unika objekt baserat på ett fält
 */
const getUniqueItemsByField = (
  items: ProcessItem[],
  field: keyof ProcessItem
): ProcessItem[] => {
  const seen = new Set<string>();
  return items.filter(item => {
    const value = String(item[field]);
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

/**
 * Ger unika objekt baserat på en kombination av fält
 */
const getUniqueItemsByFields = (
  items: ProcessItem[],
  fields: Array<keyof ProcessItem>
): ProcessItem[] => {
  const seen = new Set<string>();
  return items.filter(item => {
    const composite = fields.map(field => String(item[field])).join('|');
    if (seen.has(composite)) return false;
    seen.add(composite);
    return true;
  });
};

/**
 * Genererar frågor om huvudprocesser
 */
const generateMainProcessQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  const mainProcesses = [...new Set(data.map(item => item.Title))];
  
  if (mainProcesses.length < 4) return []; // Behöver minst 4 för alternativ
  
  // Exempel: "Vilken av följande är en huvudprocess i systemet?"
  for (let i = 0; i < Math.min(count, mainProcesses.length); i++) {
    const correctProcess = mainProcesses[i];
    
    // Skapa felaktiga alternativ (3 st)
    const otherProcesses = mainProcesses.filter(p => p !== correctProcess);
    const incorrectOptions = shuffleArray(otherProcesses).slice(0, 3);
    
    // Skapa alla alternativ och blanda
    const allOptions = shuffleArray([correctProcess, ...incorrectOptions]);
    const correctIndex = allOptions.indexOf(correctProcess);
    
    questions.push({
      id: `main-process-${i}`,
      type: 'main-process',
      questionText: "Vilken av följande är en huvudprocess i systemet?",
      answers: allOptions.map(option => ({ text: option })),
      correctAnswerIndex: correctIndex,
      explanation: `"${correctProcess}" är en av huvudprocesserna i systemet.`,
      metadata: {
        mainProcess: correctProcess
      }
    });
  }
  
  return questions;
};

/**
 * Genererar frågor om delprocesser
 */
const generateSubProcessQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  
  // Gruppera delprocesser per huvudprocess
  const processGroups: Record<string, string[]> = {};
  data.forEach(item => {
    if (!processGroups[item.Title]) {
      processGroups[item.Title] = [];
    }
    if (!processGroups[item.Title].includes(item.field_0)) {
      processGroups[item.Title].push(item.field_0);
    }
  });
  
  // Skapa frågor för varje huvudprocess som har tillräckligt med delprocesser
  const validProcesses = Object.keys(processGroups).filter(
    process => processGroups[process].length >= 4
  );
  
  if (validProcesses.length === 0) return [];
  
  for (let i = 0; i < Math.min(count, validProcesses.length); i++) {
    const mainProcess = validProcesses[i % validProcesses.length];
    const subProcesses = processGroups[mainProcess];
    
    const correctSubProcess = subProcesses[i % subProcesses.length];
    const incorrectOptions = shuffleArray(
      subProcesses.filter(p => p !== correctSubProcess)
    ).slice(0, 3);
    
    const allOptions = shuffleArray([correctSubProcess, ...incorrectOptions]);
    const correctIndex = allOptions.indexOf(correctSubProcess);
    
    questions.push({
      id: `sub-process-${i}`,
      type: 'sub-process',
      questionText: `Vilken av följande är en delprocess i "${mainProcess}"?`,
      answers: allOptions.map(option => ({ text: option })),
      correctAnswerIndex: correctIndex,
      explanation: `"${correctSubProcess}" är en delprocess inom huvudprocessen "${mainProcess}".`,
      metadata: {
        mainProcess: mainProcess,
        subProcess: correctSubProcess
      }
    });
  }
  
  return questions;
};

/**
 * Genererar frågor om moment
 */
const generateMomentQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  
  // Gruppera moment per delprocess
  const momentGroups: Record<string, ProcessItem[]> = {};
  data.forEach(item => {
    const key = `${item.Title}|${item.field_0}`;
    if (!momentGroups[key]) {
      momentGroups[key] = [];
    }
    momentGroups[key].push(item);
  });
  
  // Välj bara processer med tillräckligt många moment
  const validProcessKeys = Object.keys(momentGroups).filter(
    key => momentGroups[key].length >= 4
  );
  
  if (validProcessKeys.length === 0) return [];
  
  for (let i = 0; i < Math.min(count, validProcessKeys.length); i++) {
    const processKey = validProcessKeys[i % validProcessKeys.length];
    const [mainProcess, subProcess] = processKey.split('|');
    const moments = momentGroups[processKey];
    
    const correctItem = moments[i % moments.length];
    const correctMoment = correctItem.field_2;
    
    const incorrectItems = shuffleArray(
      moments.filter(item => item.field_2 !== correctMoment)
    ).slice(0, 3);
    
    const allOptions = shuffleArray([
      correctMoment,
      ...incorrectItems.map(item => item.field_2)
    ]);
    
    const correctIndex = allOptions.indexOf(correctMoment);
    
    questions.push({
      id: `moment-${i}`,
      type: 'moment',
      questionText: `Vilket av följande är ett moment i delprocessen "${subProcess}"?`,
      contextText: `Huvudprocess: ${mainProcess}`,
      answers: allOptions.map(option => ({ text: option })),
      correctAnswerIndex: correctIndex,
      explanation: `"${correctMoment}" är ett moment inom delprocessen "${subProcess}" i huvudprocessen "${mainProcess}".`,
      metadata: {
        mainProcess: mainProcess,
        subProcess: subProcess,
        moment: correctMoment,
        hierarchicalNumber: correctItem.hierarchicalNumber
      }
    });
  }
  
  return questions;
};

/**
 * Genererar frågor om deltagare/roller
 */
const generateParticipantQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  
  // Filtrera item med deltagare
  const itemsWithParticipants = data.filter(item => 
    (item.Deltagareochansvar1 && item.Deltagareochansvar1.trim() !== "") ||
    (item.Deltagareochansvar2 && item.Deltagareochansvar2.trim() !== "")
  );
  
  if (itemsWithParticipants.length === 0) return [];
  
  // Skapa en lista med alla unika roller
  const allRoles = new Set<string>();
  itemsWithParticipants.forEach(item => {
    if (item.Deltagareochansvar1) {
      parseParticipantsWithResponsibilities(item.Deltagareochansvar1)
        .forEach(p => allRoles.add(p.role));
    }
    if (item.Deltagareochansvar2) {
      parseParticipantsWithResponsibilities(item.Deltagareochansvar2)
        .forEach(p => allRoles.add(p.role));
    }
  });
  
  // Konvertera till array för enklare hantering
  const allRolesArray = Array.from(allRoles);
  
  // Om vi inte har tillräckligt med roller, avsluta
  if (allRolesArray.length < 4) return [];
  
  for (let i = 0; i < Math.min(count, itemsWithParticipants.length); i++) {
    const item = itemsWithParticipants[i % itemsWithParticipants.length];
    const participants1 = item.Deltagareochansvar1 
      ? parseParticipantsWithResponsibilities(item.Deltagareochansvar1) 
      : [];
    const participants2 = item.Deltagareochansvar2 
      ? parseParticipantsWithResponsibilities(item.Deltagareochansvar2) 
      : [];
    
    // Använd primärt alternativ 1, fall tillbaka till alternativ 2 om nödvändigt
    const participants = participants1.length > 0 ? participants1 : participants2;
    
    if (participants.length === 0) continue;
    
    const correctParticipant = participants[i % participants.length];
    const correctRole = correctParticipant.role;
    
    // Hitta roller som INTE finns i detta moment för felaktiga alternativ
    const momentRoles = new Set(participants.map(p => p.role));
    const incorrectRoles = allRolesArray.filter(role => !momentRoles.has(role));
    
    if (incorrectRoles.length < 3) continue; // Hoppa över om vi inte har tillräckligt med felaktiga alternativ
    
    const incorrectOptions = shuffleArray(incorrectRoles).slice(0, 3);
    const allOptions = shuffleArray([correctRole, ...incorrectOptions]);
    const correctIndex = allOptions.indexOf(correctRole);
    
    questions.push({
      id: `participant-${i}`,
      type: 'participant',
      questionText: `Vilken roll är involverad i momentet "${item.field_2}"?`,
      contextText: `Huvudprocess: ${item.Title}, Delprocess: ${item.field_0}`,
      answers: allOptions.map(option => ({ text: option })),
      correctAnswerIndex: correctIndex,
      explanation: `"${correctRole}" är en av rollerna som deltar i momentet "${item.field_2}".`,
      metadata: {
        mainProcess: item.Title,
        subProcess: item.field_0,
        moment: item.field_2,
        hierarchicalNumber: item.hierarchicalNumber
      }
    });
  }
  
  return questions;
};

/**
 * Genererar frågor om dokument
 */
const generateDocumentQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  
  // Filtrera objekt med dokument
  const itemsWithDocuments = data.filter(item => 
    item.field_4 && item.field_4.trim() !== ""
  );
  
  if (itemsWithDocuments.length === 0) return [];
  
  // Samla alla dokument från alla processer
  const allDocuments = new Set<string>();
  itemsWithDocuments.forEach(item => {
    parseDocumentLinks(item.field_4).forEach(doc => 
      allDocuments.add(doc.title)
    );
  });
  
  const allDocumentsArray = Array.from(allDocuments);
  
  // Om vi inte har tillräckligt med dokument, avsluta
  if (allDocumentsArray.length < 4) return [];
  
  for (let i = 0; i < Math.min(count, itemsWithDocuments.length); i++) {
    const item = itemsWithDocuments[i % itemsWithDocuments.length];
    const documents = parseDocumentLinks(item.field_4);
    
    if (documents.length === 0) continue;
    
    const correctDocument = documents[i % documents.length];
    
    // Hitta dokument som INTE finns i detta moment för felaktiga alternativ
    const momentDocs = new Set(documents.map(doc => doc.title));
    const incorrectDocs = allDocumentsArray.filter(doc => !momentDocs.has(doc));
    
    if (incorrectDocs.length < 3) continue; // Hoppa över om vi inte har tillräckligt med felaktiga alternativ
    
    const incorrectOptions = shuffleArray(incorrectDocs).slice(0, 3);
    const allOptions = shuffleArray([correctDocument.title, ...incorrectOptions]);
    const correctIndex = allOptions.indexOf(correctDocument.title);
    
    questions.push({
      id: `document-${i}`,
      type: 'document',
      questionText: `Vilket dokument är kopplat till momentet "${item.field_2}"?`,
      contextText: `Huvudprocess: ${item.Title}, Delprocess: ${item.field_0}`,
      answers: allOptions.map(option => ({ text: option })),
      correctAnswerIndex: correctIndex,
      explanation: `"${correctDocument.title}" är ett av dokumenten som är kopplade till momentet "${item.field_2}".`,
      metadata: {
        mainProcess: item.Title,
        subProcess: item.field_0,
        moment: item.field_2,
        hierarchicalNumber: item.hierarchicalNumber
      }
    });
  }
  
  return questions;
};

/**
 * Genererar frågor om viktiga moment
 */
const generateImportantQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  
  // Filtrera objekt med viktigt-markering
  const importantItems = data.filter(item => 
    item.Viktigt && item.Viktigt.trim() !== ""
  );
  
  // Om vi inte har viktiga objekt, avsluta
  if (importantItems.length === 0) return [];
  
  // Skapa en pool av moment utan viktigt-markering för felaktiga alternativ
  const nonImportantItems = data.filter(item => 
    !item.Viktigt || item.Viktigt.trim() === ""
  );
  
  for (let i = 0; i < Math.min(count, importantItems.length); i++) {
    const item = importantItems[i % importantItems.length];
    
    // Skapa en fråga om vad som är viktigt i detta moment
    questions.push({
      id: `important-detail-${i}`,
      type: 'important',
      questionText: `Vad är viktigt att tänka på i momentet "${item.field_2}"?`,
      contextText: `Huvudprocess: ${item.Title}, Delprocess: ${item.field_0}`,
      answers: [
        { text: item.Viktigt || "" },
        ...generateIncorrectViktigtAnswers(nonImportantItems, 3)
      ],
      correctAnswerIndex: 0,
      explanation: `Det viktiga att tänka på i momentet "${item.field_2}" är: "${item.Viktigt}".`,
      metadata: {
        mainProcess: item.Title,
        subProcess: item.field_0,
        moment: item.field_2,
        hierarchicalNumber: item.hierarchicalNumber
      }
    });
    
    // Skapa även en fråga om vilket moment som har en specifik viktigt-markering
    if (importantItems.length >= 4) {
      const correctMoment = item.field_2;
      const incorrectMoments = shuffleArray(
        importantItems
          .filter(otherItem => otherItem.field_2 !== correctMoment)
          .map(item => item.field_2)
      ).slice(0, 3);
      
      const allOptions = shuffleArray([correctMoment, ...incorrectMoments]);
      const correctIndex = allOptions.indexOf(correctMoment);
      
      questions.push({
        id: `important-moment-${i}`,
        type: 'important',
        questionText: `Vilket moment har följande viktiga notering: "${truncateText(item.Viktigt || "", 50)}"?`,
        answers: allOptions.map(option => ({ text: option })),
        correctAnswerIndex: correctIndex,
        explanation: `Momentet "${correctMoment}" har noteringen "${item.Viktigt}".`,
        metadata: {
          mainProcess: item.Title,
          subProcess: item.field_0,
          moment: item.field_2,
          hierarchicalNumber: item.hierarchicalNumber
        }
      });
    }
  }
  
  return questions;
};

/**
 * Genererar alternativa "viktigt"-text för felaktiga svarsalternativ
 */
const generateIncorrectViktigtAnswers = (
  items: ProcessItem[],
  count: number
): QuizAnswer[] => {
  const answers: QuizAnswer[] = [];
  
  // Om vi har beskrivningar, använd dem för att generera realistiska viktigt-svar
  const itemsWithDescriptions = items.filter(item => 
    item.field_3 && item.field_3.trim() !== ""
  );
  
  if (itemsWithDescriptions.length >= count) {
    // Använd beskrivningar som bas för felaktiga viktigtmarkeringar
    const randomItems = shuffleArray(itemsWithDescriptions).slice(0, count);
    answers.push(...randomItems.map(item => ({
      text: `Se till att ${truncateText(item.field_3 || "", 40).toLowerCase()}`
    })));
  } else {
    // Generera generiska felaktiga svar
    const genericAnswers = [
      "Se till att dokumentera alla steg i processen.",
      "Kontrollera att alla berörda parter är informerade.",
      "Säkerställ att alla dokument är uppdaterade innan du fortsätter.",
      "Kontakta ansvarig chef innan processen påbörjas.",
      "Granska alla dokument noggrant innan du går vidare.",
      "Se till att du har alla nödvändiga behörigheter.",
      "Kontrollera att alla uppgifter är korrekt ifyllda.",
      "Involvera alla berörda avdelningar vid behov."
    ];
    
    answers.push(...shuffleArray(genericAnswers)
      .slice(0, count)
      .map(text => ({ text }))
    );
  }
  
  return answers;
};

/**
 * Genererar frågor om detaljerade beskrivningar
 */
const generateDetailedDescriptionQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  
  // Filtrera objekt med detaljerade beskrivningar
  const itemsWithDetailedDesc = data.filter(item => 
    item.Detaljeradbeskrivning && item.Detaljeradbeskrivning.trim() !== ""
  );
  
  if (itemsWithDetailedDesc.length === 0) return [];
  
  for (let i = 0; i < Math.min(count, itemsWithDetailedDesc.length); i++) {
    const item = itemsWithDetailedDesc[i % itemsWithDetailedDesc.length];
    
    // Skapa en fråga om innehållet i den detaljerade beskrivningen
    const description = item.Detaljeradbeskrivning || "";
    const descriptionSummary = truncateText(description, 100);
    
    // Skapa felaktiga alternativ från andra beskrivningar
    const otherDescriptions = itemsWithDetailedDesc
      .filter(otherItem => otherItem !== item)
      .map(otherItem => otherItem.Detaljeradbeskrivning || "");
    
    if (otherDescriptions.length < 3) continue; // Hoppa över om vi inte har tillräckligt med alternativ
    
    const incorrectOptions = shuffleArray(otherDescriptions)
      .slice(0, 3)
      .map(desc => truncateText(desc, 100));
    
    const allOptions = shuffleArray([descriptionSummary, ...incorrectOptions]);
    const correctIndex = allOptions.indexOf(descriptionSummary);
    
    questions.push({
      id: `detailed-description-${i}`,
      type: 'detailed-description',
      questionText: `Vilken detaljerad beskrivning hör till momentet "${item.field_2}"?`,
      contextText: `Huvudprocess: ${item.Title}, Delprocess: ${item.field_0}`,
      answers: allOptions.map(option => ({ text: option })),
      correctAnswerIndex: correctIndex,
      explanation: `Den korrekta detaljerade beskrivningen för momentet "${item.field_2}" börjar med: "${truncateText(description, 50)}..."`,
      metadata: {
        mainProcess: item.Title,
        subProcess: item.field_0,
        moment: item.field_2,
        hierarchicalNumber: item.hierarchicalNumber
      }
    });
  }
  
  return questions;
};

/**
 * Genererar frågor av typen "formuleringar i processer"
 * Ny förbättrad version som fokuserar på hela meningar och bättre grammatik
 */
const generateFillInBlankQuestions = (
    data: ProcessItem[],
    count: number
  ): EnhancedQuizQuestion[] => {
    const questions: EnhancedQuizQuestion[] = [];
    
    // Filtrera objekt med tillräckligt långa beskrivningar
    const itemsWithLongDesc = data.filter(item => 
      item.field_3 && item.field_3.trim().length > 30
    );
    
    if (itemsWithLongDesc.length === 0) return [];
    
    for (let i = 0; i < Math.min(count, itemsWithLongDesc.length); i++) {
      const item = itemsWithLongDesc[i % itemsWithLongDesc.length];
      const description = item.field_3 || "";
      
      // Dela upp beskrivningen i meningar
      const sentences = description
        .split(/(?<=[.!?])\s+/)  // Dela vid punkter, utropstecken och frågetecken följt av mellanslag
        .map(s => s.trim())
        .filter(s => s.length > 15);  // Filtrera bort för korta meningar
      
      if (sentences.length === 0) continue;
      
      // Välj en mening att arbeta med
      const selectedSentence = sentences[Math.floor(Math.random() * sentences.length)];
      
      // Det korrekta alternativet är den ursprungliga meningen
      const correctOption = selectedSentence;
      
      // Skapa tre trovärdiga men felaktiga alternativ - hela meningar
      // som har samma grundstruktur men väsentligt annorlunda betydelse
      const incorrectOptions: string[] = [];
      
      // Analysera meningsstrukturen och identifiera viktiga verb/uttryck
      const words = selectedSentence.split(/\s+/);
      
      // Lista över verb och viktiga fraser som kan bytas ut
      const actionVerbs = [
        ["upprättas", "skapas", "produceras", "utformas"],
        ["skickas", "levereras", "överlämnas", "distribueras"],
        ["granskas", "kontrolleras", "utvärderas", "verifieras"],
        ["godkänns", "accepteras", "fastställs", "bekräftas"],
        ["rapporteras", "dokumenteras", "registreras", "noteras"],
        ["analyseras", "undersöks", "utvärderas", "granskas"],
        ["planeras", "schemaläggs", "struktureras", "organiseras"]
      ];
      
      // Lista över olika processobjekt (substantiv) att byta ut
      const processObjects = [
        ["avtal", "kontrakt", "överenskommelse", "åtagande"],
        ["dokument", "underlag", "handlingar", "material"],
        ["process", "rutin", "arbetsgång", "tillvägagångssätt"],
        ["möte", "sammanträde", "sammankomst", "träff"],
        ["rapport", "redovisning", "sammanställning", "dokumentation"],
        ["beräkning", "kalkyl", "uppskattning", "estimering"],
        ["granskning", "genomgång", "kontroll", "utvärdering"]
      ];
      
      // Lista över olika adjektiv att byta ut
      const adjectives = [
        ["omfattande", "detaljerad", "grundlig", "ingående"],
        ["viktig", "central", "kritisk", "avgörande"],
        ["nödvändig", "obligatorisk", "väsentlig", "grundläggande"],
        ["komplett", "fullständig", "heltäckande", "uttömmande"],
        ["specifik", "särskild", "specifik", "utpekad"],
        ["teknisk", "fackmannamässig", "specialiserad", "expertinriktad"]
      ];
      
      // Identifiera nyckelverb i meningen
      const foundVerbs: string[] = [];
      const foundNouns: string[] = [];
      const foundAdjectives: string[] = [];
      
      // Hitta matchande verb/substantiv i meningen
      words.forEach(word => {
        actionVerbs.forEach(group => {
          if (group.includes(word.toLowerCase())) {
            foundVerbs.push(word);
          }
        });
        
        processObjects.forEach(group => {
          if (group.includes(word.toLowerCase())) {
            foundNouns.push(word);
          }
        });
        
        adjectives.forEach(group => {
          if (group.includes(word.toLowerCase())) {
            foundAdjectives.push(word);
          }
        });
      });
      
      // Generera tre unika varianter genom att byta ut nyckelverb eller substantiv
      const generateVariant = () => {
        let variant = selectedSentence;
        
        // Vi prioriterar att byta ut verb eftersom de ändrar betydelsen mest
        if (foundVerbs.length > 0) {
          const verbToReplace = foundVerbs[Math.floor(Math.random() * foundVerbs.length)];
          const verbGroup = actionVerbs.find(group => group.includes(verbToReplace.toLowerCase()));
          
          if (verbGroup) {
            const alternativeVerbs = verbGroup.filter(v => v.toLowerCase() !== verbToReplace.toLowerCase());
            if (alternativeVerbs.length > 0) {
              const replacement = alternativeVerbs[Math.floor(Math.random() * alternativeVerbs.length)];
              variant = variant.replace(new RegExp(`\\b${verbToReplace}\\b`, 'i'), replacement);
            }
          }
        } 
        // Om inga verb hittades, försök med substantiv
        else if (foundNouns.length > 0) {
          const nounToReplace = foundNouns[Math.floor(Math.random() * foundNouns.length)];
          const nounGroup = processObjects.find(group => group.includes(nounToReplace.toLowerCase()));
          
          if (nounGroup) {
            const alternativeNouns = nounGroup.filter(n => n.toLowerCase() !== nounToReplace.toLowerCase());
            if (alternativeNouns.length > 0) {
              const replacement = alternativeNouns[Math.floor(Math.random() * alternativeNouns.length)];
              variant = variant.replace(new RegExp(`\\b${nounToReplace}\\b`, 'i'), replacement);
            }
          }
        }
        // Sista utvögen är att byta adjektiv
        else if (foundAdjectives.length > 0) {
          const adjToReplace = foundAdjectives[Math.floor(Math.random() * foundAdjectives.length)];
          const adjGroup = adjectives.find(group => group.includes(adjToReplace.toLowerCase()));
          
          if (adjGroup) {
            const alternativeAdj = adjGroup.filter(a => a.toLowerCase() !== adjToReplace.toLowerCase());
            if (alternativeAdj.length > 0) {
              const replacement = alternativeAdj[Math.floor(Math.random() * alternativeAdj.length)];
              variant = variant.replace(new RegExp(`\\b${adjToReplace}\\b`, 'i'), replacement);
            }
          }
        }
        
        return variant;
      };
      
      // Om vi inte hittar några nyckelord att byta ut
      // Använd fallback-metoden - ändra prepositioner och/eller lägg till/ta bort ord
      const fallbackVariants = [
        // Varianter där vi lägger till ord som ändrar betydelsen
        selectedSentence.replace(/\b(för|till|med|av|på|i)\b/g, match => {
          const preps = ["för", "till", "med", "av", "på", "i"].filter(p => p !== match);
          return preps[Math.floor(Math.random() * preps.length)];
        }),
        
        // Varianter med semantiskt betydande verb
        selectedSentence.includes("och") 
          ? selectedSentence.replace(/\boch\b/, "men inte")
          : selectedSentence.replace(/\b(ska|skall|bör|måste|kan)\b/g, "bör inte"),
          
        // Variant med negation om det inte redan finns
        selectedSentence.includes("inte") 
          ? selectedSentence.replace(/\binte\b/, "") 
          : selectedSentence.replace(/\b(är|ska|skall|bör|måste|kan)\b/, "$1 inte"),
          
        // Variant där ett viktigt ord byts mot sin motsats
        selectedSentence.replace(/\b(godkänns|godkänd|accepteras|accepterad)\b/g, "avvisas")
          .replace(/\b(skickas|skickad|sänds|sänd)\b/g, "behålls")
          .replace(/\b(viktig|central|kritisk)\b/g, "valfri")
          .replace(/\b(ska|skall|måste)\b/g, "behöver inte")
      ];
      
      // Försök generera tre unika varianter först
      for (let j = 0; j < 3; j++) {
        let variant = generateVariant();
        
        // Om vi inte lyckades göra en bra variant (dvs den är samma som originalet)
        // eller den är identisk med någon vi redan har lagt till
        if (variant === correctOption || incorrectOptions.includes(variant)) {
          // Använd en fallback-variant istället
          const fallbackIndex = j % fallbackVariants.length;
          variant = fallbackVariants[fallbackIndex];
          
          // Om även fallbacken är identisk med originalet eller redan tillagd
          if (variant === correctOption || incorrectOptions.includes(variant)) {
            // Skapa en variant genom att ändra ett verb i början av meningen
            const firstWords = words.slice(0, 3).join(" ");
            variant = selectedSentence.replace(firstWords, 
              j === 0 ? "Detta görs för att" : 
              j === 1 ? "Man behöver kontrollera" : 
              "Det är viktigt att");
          }
        }
        
        // Slutligen, om vi misslyckats med att få en unik variant
        if (variant !== correctOption && !incorrectOptions.includes(variant)) {
          incorrectOptions.push(variant);
        } else {
          // Om alla andra försök misslyckas, skapa en helgarderad variant
          incorrectOptions.push(`${j === 0 ? "Man ska kontrollera" : j === 1 ? "Det är viktigt att verifiera" : "Det behövs en granskning av"} ${
            foundNouns.length > 0 ? foundNouns[0] : "dokumentet"
          }`);
        }
      }
      
      // Blanda alla alternativ
      const allOptions = shuffleArray([correctOption, ...incorrectOptions.slice(0, 3)]);
      const correctIndex = allOptions.indexOf(correctOption);
      
      // Skapa frågan utan att visa luckor i kontexten
      questions.push({
        id: `process-formulation-${i}`,
        type: 'fill-in-blank',
        questionText: `Vad är den korrekta formuleringen för momentet "${item.field_2}"?`,
        contextText: `Välj den korrekta beskrivningen för det aktuella momentet.`,
        answers: allOptions.map(option => ({ text: option })),
        correctAnswerIndex: correctIndex,
        explanation: `Den korrekta formuleringen är: "${correctOption}"`,
        metadata: {
          mainProcess: item.Title,
          subProcess: item.field_0,
          moment: item.field_2,
          hierarchicalNumber: item.hierarchicalNumber
        }
      });
    }
    
    return questions;
  };

/**
 * Genererar frågor om sekvens/ordning av moment inom en process
 */
const generateSequenceQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  
  // Gruppera moment per delprocess
  const momentGroups: Record<string, ProcessItem[]> = {};
  data.forEach(item => {
    const key = `${item.Title}|${item.field_0}`;
    if (!momentGroups[key]) {
      momentGroups[key] = [];
    }
    momentGroups[key].push(item);
  });
  
  // Välj bara processer med tillräckligt många moment
  const validProcessKeys = Object.keys(momentGroups).filter(
    key => momentGroups[key].length >= 4
  );
  
  if (validProcessKeys.length === 0) return [];
  
  for (let i = 0; i < Math.min(count, validProcessKeys.length); i++) {
    const processKey = validProcessKeys[i % validProcessKeys.length];
    const [mainProcess, subProcess] = processKey.split('|');
    const moments = momentGroups[processKey];
    
    // Sortera moment efter hierarkiskt nummer
    const sortedMoments = [...moments].sort((a, b) => {
      if (!a.hierarchicalNumber || !b.hierarchicalNumber) return 0;
      return a.hierarchicalNumber.localeCompare(b.hierarchicalNumber);
    });
    
    if (sortedMoments.length < 4) continue;
    
    // Välj 4 moment i sekvens med rätt ordning
    const startIndex = Math.floor(Math.random() * (sortedMoments.length - 4 + 1));
    const sequenceMoments = sortedMoments.slice(startIndex, startIndex + 4);
    
    // Välj ett slumpmässigt moment från sekvensen att fråga om
    const targetIndex = Math.floor(Math.random() * sequenceMoments.length);
    const targetMoment = sequenceMoments[targetIndex];
    
    // Skapa en fråga om momentets position i sekvensen
    const positions = ["första", "andra", "tredje", "fjärde"];
    const correctPosition = positions[targetIndex];
    
    // Shuffla positionerna för svarsalternativen
    const shuffledPositions = shuffleArray([...positions]);
    const correctIndex = shuffledPositions.indexOf(correctPosition);
    
    questions.push({
      id: `sequence-${i}`,
      type: 'sequence',
      questionText: `I vilken position kommer momentet "${targetMoment.field_2}" i delprocessen "${subProcess}"?`,
      contextText: `Huvudprocess: ${mainProcess}. Sekvensen innehåller momenten: ${sequenceMoments.map(m => `"${m.field_2}"`).join(", ")}`,
      answers: shuffledPositions.map(position => ({ 
        text: `Det är det ${position} momentet i sekvensen` 
      })),
      correctAnswerIndex: correctIndex,
      explanation: `"${targetMoment.field_2}" är det ${correctPosition} momentet i den visade sekvensen.`,
      metadata: {
        mainProcess: mainProcess,
        subProcess: subProcess,
        moment: targetMoment.field_2,
        hierarchicalNumber: targetMoment.hierarchicalNumber
      }
    });
  }
  
  return questions;
};

/**
 * Genererar frågor om relationer mellan deltagare och dokument
 */
const generateRelationQuestions = (
  data: ProcessItem[],
  count: number
): EnhancedQuizQuestion[] => {
  const questions: EnhancedQuizQuestion[] = [];
  
  // Filtrera objekt med både deltagare och dokument
  const itemsWithBoth = data.filter(item => 
    ((item.Deltagareochansvar1 && item.Deltagareochansvar1.trim() !== "") || 
     (item.Deltagareochansvar2 && item.Deltagareochansvar2.trim() !== "")) &&
    (item.field_4 && item.field_4.trim() !== "")
  );
  
  if (itemsWithBoth.length === 0) return [];
  
  for (let i = 0; i < Math.min(count, itemsWithBoth.length); i++) {
    const item = itemsWithBoth[i % itemsWithBoth.length];
    
    // Hämta deltagare (föredra alternativ 1, fall tillbaka till 2)
    const participants = item.Deltagareochansvar1
      ? parseParticipantsWithResponsibilities(item.Deltagareochansvar1)
      : parseParticipantsWithResponsibilities(item.Deltagareochansvar2 || "");
    
    if (participants.length === 0) continue;
    
    // Hämta dokument
    const documents = parseDocumentLinks(item.field_4);
    if (documents.length === 0) continue;
    
    // Välj en slumpmässig deltagare och ett dokument
    const participant = participants[Math.floor(Math.random() * participants.length)];
    const document = documents[Math.floor(Math.random() * documents.length)];
    
    // Skapa fråga om vilken roll som arbetar med dokumentet
    const allRoles = new Set<string>();
    
    // Samla alla roller från alla moment
    itemsWithBoth.forEach(item => {
      if (item.Deltagareochansvar1) {
        parseParticipantsWithResponsibilities(item.Deltagareochansvar1)
          .forEach(p => allRoles.add(p.role));
      }
      if (item.Deltagareochansvar2) {
        parseParticipantsWithResponsibilities(item.Deltagareochansvar2)
          .forEach(p => allRoles.add(p.role));
      }
    });
    
    // Ta bort den korrekta rollen
    allRoles.delete(participant.role);
    
    // Om vi inte har tillräckligt med alternativ, hoppa över
    if (allRoles.size < 3) continue;
    
    const incorrectRoles = shuffleArray(Array.from(allRoles)).slice(0, 3);
    const allOptions = shuffleArray([participant.role, ...incorrectRoles]);
    const correctIndex = allOptions.indexOf(participant.role);
    
    questions.push({
      id: `relation-${i}`,
      type: 'relation',
      questionText: `Vilken roll arbetar med dokumentet "${document.title}" i momentet "${item.field_2}"?`,
      contextText: `Huvudprocess: ${item.Title}, Delprocess: ${item.field_0}`,
      answers: allOptions.map(option => ({ text: option })),
      correctAnswerIndex: correctIndex,
      explanation: `Rollen "${participant.role}" arbetar med dokumentet "${document.title}" i momentet "${item.field_2}".`,
      metadata: {
        mainProcess: item.Title,
        subProcess: item.field_0,
        moment: item.field_2,
        hierarchicalNumber: item.hierarchicalNumber
      }
    });
  }
  
  return questions;
};

/**
 * Tilldelar svårighetsgrad till frågor baserat på typ och innehåll
 */
const assignDifficulty = (
  questions: EnhancedQuizQuestion[],
  targetDifficulty: QuizDifficulty
): EnhancedQuizQuestion[] => {
  // Om target är mixed, tilldela en blandning av svårighetsgrader
  if (targetDifficulty === 'mixed') {
    return questions.map((question, index) => {
      let difficulty: 'easy' | 'medium' | 'hard';
      
      // Basera svårigheten på frågetyp och index
      switch(question.type) {
        case 'main-process':
        case 'sub-process':
          difficulty = 'easy';
          break;
          
        case 'moment':
        case 'document':
        case 'detailed-description':
          difficulty = 'medium';
          break;
          
        case 'participant':
        case 'important':
        case 'fill-in-blank':
        case 'sequence':
        case 'relation':
          difficulty = 'hard';
          break;
          
        default:
          difficulty = index % 3 === 0 ? 'easy' : (index % 3 === 1 ? 'medium' : 'hard');
      }
      
      return { ...question, difficulty };
    });
  } else {
    // Annars tilldela alla frågor den specificerade svårighetsgraden
    return questions.map(question => ({
      ...question,
      difficulty: targetDifficulty
    }));
  }
};

/**
 * Hjälpfunktion för att trunkera text till en viss längd
 */
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

/**
 * Hjälpfunktion för att blanda en array
 */
const shuffleArray = <T>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};